import { useState, useEffect, useCallback } from 'react'
import { ChangelogData } from '../types'

interface UseChangelogDataOptions {
  endpoint?: string
  data?: ChangelogData
}

interface UseChangelogDataReturn {
  data: ChangelogData | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export const useChangelogData = ({
  endpoint,
  data,
}: UseChangelogDataOptions): UseChangelogDataReturn => {
  const [fetchedData, setFetchedData] = useState<ChangelogData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!endpoint) return

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(endpoint)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const result: ChangelogData = await response.json()
      setFetchedData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }, [endpoint])

  useEffect(() => {
    if (data) {
      // If data is provided directly, use it
      setFetchedData(data)
      setLoading(false)
      setError(null)
    } else if (endpoint) {
      // If endpoint is provided, fetch data
      fetchData()
    }
  }, [data, endpoint, fetchData])

  return {
    data: data || fetchedData,
    loading,
    error,
    refetch: fetchData,
  }
}
